import { useEffect, useMemo, useState, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button/index.js';
import { useToast } from '../components/ui/Toast/index.js';
import {
  useCreateDataViewerJob,
  useUpdateDataViewerJob,
  useDeleteDataViewerJob,
  useDataViewerJobDetail,
  useDataViewerJobs,
  useDatabaseList,
  usePreviewJobQuery,
} from '../hooks/useDataViewer.js';
import { ApiError } from '../lib/api.js';
import { inferFilterType } from '../lib/data-viewer-filter.js';
import {
  FilterableColumnsEditor,
  buildInitialFilterableColumnRows,
  collectFilterableColumnNames,
  collectDatabaseFilterableColumns,
  useFilterableColumnRows,
} from '../components/features/data-viewer/FilterableColumnsEditor/index.js';
import type { FilterableColumn } from '../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business クエリジョブ登録・編集ページ。ローカル開発環境でのみサイドバーに表示
 * @graph-connects useCreateDataViewerJob [calls] ジョブ登録ミューテーション
 * @graph-connects useUpdateDataViewerJob [calls] ジョブ更新ミューテーション
 * @graph-connects useDatabaseList [calls] DB選択肢をdb_credentialsから取得
 */
export const Route = createFileRoute('/data-viewer-register')({
  component: DataViewerRegisterPage,
});

/** @graph-connects none */
export function DataViewerRegisterPage() {
  const { t } = useTranslation();
  const { showToast, dismissToast, toasts } = useToast();
  const createJob = useCreateDataViewerJob();
  const updateJob = useUpdateDataViewerJob();
  const deleteJob = useDeleteDataViewerJob();
  const previewJobQuery = usePreviewJobQuery();
  const { data: databaseList } = useDatabaseList();
  const { data: jobList } = useDataViewerJobs();

  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: jobDetail, isLoading: isDetailLoading } = useDataViewerJobDetail(editingId);

  const [id, setId] = useState('');
  const [label, setLabel] = useState('');
  const [sql, setSql] = useState('');
  const [datasetId, setDatasetId] = useState('');
  const [description, setDescription] = useState('');
  const [allowedEmails, setAllowedEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const columnEditor = useFilterableColumnRows({
    sql,
    datasetId,
    onError: (reason) => {
      setValidationErrors([t('dataViewerRegister.previewFailedWithReason', { reason })]);
    },
  });
  const filterableColumnRows = columnEditor.rows;
  const emailInputRef = useRef<HTMLInputElement>(null);
  const loadedJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!jobDetail) return;
    if (loadedJobIdRef.current === jobDetail.id) return;
    loadedJobIdRef.current = jobDetail.id;
    setId(jobDetail.id);
    setLabel(jobDetail.label);
    setSql(jobDetail.sql);
    setDatasetId(jobDetail.datasetId ?? '');
    setDescription(jobDetail.description ?? '');
    const legacyEmailsFromGroups = (jobDetail.allowedGroups ?? []).filter((v) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    );
    setAllowedEmails([...new Set([...(jobDetail.allowedEmails ?? []), ...legacyEmailsFromGroups])]);
    // 保存済み filterableColumns は、保存済みの型バッジ付き行として表示する。
    columnEditor.setRows(buildInitialFilterableColumnRows(jobDetail.filterableColumns ?? []));
  }, [jobDetail]);

  const resetToNew = () => {
    loadedJobIdRef.current = null;
    setEditingId(null);
    setId('');
    setLabel('');
    setSql('');
    setDatasetId('');
    setDescription('');
    setAllowedEmails([]);
    setEmailInput('');
    columnEditor.setRows([]);
    setValidationErrors([]);
  };

  const isEditing = !!editingId;

  const addEmail = () => {
    const input = emailInput.trim();
    if (!input) return;
    if (!input.endsWith('@air-closet.com')) {
      showToast('warning', t('dataViewerRegister.permissionsEmailHelp'));
      return;
    }
    if (allowedEmails.includes(input)) return;
    toasts
      .filter((t) => t.type === 'warning')
      .forEach((t) => {
        dismissToast(t.id);
      });
    setAllowedEmails((prev) => [...prev, input]);
    setEmailInput('');
    emailInputRef.current?.focus();
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  const dbOptions = useMemo(() => databaseList ?? [], [databaseList]);

  /**
   * 送信するフィルタ対象カラムを組み立てる。
   *
   * DB 内カラムから選んだ行は、選択時に `DESCRIBE` の型表記から型が確定しているのでそのまま使う。
   * 手入力の行だけは、SQL をプレビュー実行して「実行結果にそのカラムがあるか」を確認しつつ
   * 型を実データから {@link inferFilterType} で判定する（手入力行の型判定はここが唯一のタイミング）。
   */
  const buildFilterableColumnsForSubmit = async (): Promise<{
    filterableColumns: FilterableColumn[];
    errors: string[];
  }> => {
    const databaseColumns = collectDatabaseFilterableColumns(filterableColumnRows);
    const columnNames = collectFilterableColumnNames(filterableColumnRows);
    if (columnNames.length === 0) return { filterableColumns: databaseColumns, errors: [] };

    let previewResult;
    try {
      previewResult = await previewJobQuery.mutateAsync({ sql, datasetId });
    } catch (error) {
      // API 側は「複数ステートメントは実行できません」等の具体的な理由を返すため、
      // 固定文で潰さず必ず本文を出す（原因が分からず切り分け不能になるのを防ぐ）。
      const reason = error instanceof Error ? error.message : String(error);
      return {
        filterableColumns: [],
        errors: [t('dataViewerRegister.previewFailedWithReason', { reason })],
      };
    }

    const filterableColumns: FilterableColumn[] = [...databaseColumns];
    const errors: string[] = [];
    for (const column of columnNames) {
      if (!previewResult.columns.includes(column)) {
        errors.push(t('dataViewer.filter.errorColumnNotFound', { column }));
        continue;
      }
      filterableColumns.push({ column, type: inferFilterType(previewResult.rows, column) });
    }
    return { filterableColumns, errors };
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationErrors([]);

    const { filterableColumns, errors } = await buildFilterableColumnsForSubmit();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    const payload = {
      label,
      sql,
      ...(datasetId ? { datasetId } : {}),
      ...(description ? { description } : {}),
      ...(allowedEmails.length ? { allowedEmails } : {}),
      ...(filterableColumns.length ? { filterableColumns } : {}),
    };

    if (isEditing) {
      updateJob.mutate(
        { id: editingId, input: payload },
        {
          onSuccess: () => {
            showToast('success', t('dataViewerRegister.updateSuccess'));
          },
          onError: (error) => {
            showToast('error', error instanceof Error ? error.message : String(error));
          },
        },
      );
    } else {
      createJob.mutate(
        { id, ...payload },
        {
          onSuccess: () => {
            showToast('success', t('dataViewerRegister.success'));
            resetToNew();
          },
          onError: (error) => {
            const message = error instanceof Error ? error.message : String(error);
            const isConflict = error instanceof ApiError && error.status === 409;
            showToast('error', isConflict ? t('dataViewerRegister.conflict') : message);
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (!editingId || !window.confirm(t('dataViewerRegister.deleteConfirm', { label }))) return;
    deleteJob.mutate(editingId, {
      onSuccess: () => {
        showToast('success', t('dataViewerRegister.deleteSuccess'));
        resetToNew();
      },
      onError: (error) => {
        showToast('error', error instanceof Error ? error.message : String(error));
      },
    });
  };

  const isPending =
    createJob.isPending || updateJob.isPending || deleteJob.isPending || previewJobQuery.isPending;

  const inputClass =
    'w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]';

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      <header>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('dataViewerRegister.title')}
        </h1>
      </header>

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
            e.preventDefault();
          }
        }}
        className="glass-card p-6 space-y-5"
      >
        {/* ジョブ選択 */}
        <div className="flex items-center gap-3 pb-2 border-b border-[var(--color-border)]">
          <select
            value={editingId ?? ''}
            onChange={(e) => {
              if (!e.target.value) {
                resetToNew();
              } else {
                setEditingId(e.target.value);
              }
            }}
            className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]"
          >
            <option value="">{t('dataViewerRegister.selectExisting')}</option>
            {jobList?.jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.label}（{job.id}）
              </option>
            ))}
          </select>
          {isEditing && (
            <button
              type="button"
              onClick={resetToNew}
              className="px-3 py-2 rounded-lg text-sm border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)] transition-colors whitespace-nowrap"
            >
              {t('dataViewerRegister.newMode')}
            </button>
          )}
        </div>

        {isDetailLoading && (
          <p className="text-sm text-[var(--color-text-muted)] text-center py-4">
            {t('common.loading')}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 左カラム: 基本情報 + SQL */}
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-[var(--color-text-secondary)]">
                {t('dataViewerRegister.id')}
              </label>
              <input
                type="text"
                required
                readOnly={isEditing}
                value={id}
                onChange={(e) => {
                  setId(e.target.value);
                }}
                placeholder="rental_history"
                pattern="[a-zA-Z0-9_]+"
                className={`${inputClass} ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              {!isEditing && (
                <p className="text-xs text-[var(--color-text-muted)]">
                  {t('dataViewerRegister.idHelp')}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-[var(--color-text-secondary)]">
                {t('dataViewerRegister.label')}
              </label>
              <input
                type="text"
                required
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                }}
                placeholder={t('dataViewerRegister.labelPlaceholder')}
                className={inputClass}
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-[var(--color-text-secondary)]">
                {t('dataViewerRegister.description')}
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                className={inputClass}
              />
            </div>

            {dbOptions.length > 0 && (
              <div className="space-y-1">
                <label className="text-sm font-medium text-[var(--color-text-secondary)]">
                  {t('dataViewerRegister.db')}
                </label>
                <select
                  required
                  value={datasetId}
                  onChange={(e) => {
                    setDatasetId(e.target.value);
                  }}
                  className={inputClass}
                >
                  <option value="" disabled>
                    {t('dataViewerRegister.selectDbPlaceholder')}
                  </option>
                  {dbOptions.map((db) => (
                    <option key={db} value={db}>
                      {db}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-1">
              <label className="text-sm font-medium text-[var(--color-text-secondary)]">
                {t('dataViewerRegister.sql')}
              </label>
              <textarea
                required
                value={sql}
                onChange={(e) => {
                  setSql(e.target.value);
                }}
                rows={12}
                placeholder={
                  'SELECT\n  user_id\n  ,created_at\nFROM aircloset_prod.users\nLIMIT 100'
                }
                className={`${inputClass} font-mono resize-y`}
              />
            </div>
          </div>

          {/* 右カラム: 権限設定 */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t('dataViewerRegister.permissions')}
            </p>

            <div className="space-y-2 rounded-lg border border-[var(--color-border)] p-4">
              <p className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
                {t('dataViewerRegister.permissionsEmails')}
              </p>
              {allowedEmails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {allowedEmails.map((email) => (
                    <span
                      key={email}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)]"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => {
                          setAllowedEmails((prev) => prev.filter((e) => e !== email));
                        }}
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors"
                        aria-label={t('dataViewerRegister.removeEmail', { email })}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={emailInputRef}
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                  }}
                  onKeyDown={handleEmailKeyDown}
                  placeholder={t('dataViewerRegister.permissionsEmailPlaceholder')}
                  className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]"
                />
                <button
                  type="button"
                  onClick={addEmail}
                  className="px-3 py-1.5 rounded-lg text-sm border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-border-hover)] transition-colors whitespace-nowrap"
                >
                  {t('common.add')}
                </button>
              </div>
            </div>

            {/* フィルタ（任意） */}
            <FilterableColumnsEditor
              rows={filterableColumnRows}
              tables={columnEditor.tables}
              columnsByTable={columnEditor.columnsByTable}
              isLoadingTables={columnEditor.isLoadingTables}
              loadingColumnsTable={columnEditor.loadingColumnsTable}
              onAddRow={columnEditor.addRow}
              onAddDatabaseRow={columnEditor.addDatabaseRow}
              onRemoveRow={columnEditor.removeRow}
              onColumnNameChange={columnEditor.updateColumnName}
              onTableChange={columnEditor.updateTable}
              onDatabaseColumnChange={columnEditor.updateDatabaseColumn}
            />
          </div>
        </div>

        <div className="flex justify-between items-end pt-2">
          {isEditing && (
            <Button type="button" variant="danger" onClick={handleDelete} disabled={isPending}>
              {t('common.delete')}
            </Button>
          )}
          <div className="flex flex-col items-end gap-2 ml-auto">
            {validationErrors.length > 0 && (
              <div className="text-sm text-[var(--color-error)] text-right space-y-1">
                {validationErrors.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            )}
            <Button type="submit" loading={isPending} disabled={isPending}>
              {isPending
                ? isEditing
                  ? t('dataViewerRegister.updating')
                  : t('dataViewerRegister.registering')
                : isEditing
                  ? t('dataViewerRegister.update')
                  : t('dataViewerRegister.register')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
